import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlatformConnection } from '../lib/api';
import Button from './Button';
import Modal from './Modal';

interface PlatformConnectionCardProps {
  connection: PlatformConnection;
  onVerify: (connectionId: string) => void;
  onDisconnect: (connectionId: string) => void;
  isVerifying?: boolean;
  isDisconnecting?: boolean;
}

// Platform display names and colors
const PLATFORM_CONFIG: Record<string, { name: string; color: string; icon: string }> = {
  linkedin_company: { name: 'LinkedIn Company', color: 'bg-[#0A66C2]', icon: 'in' },
  linkedin_personal: { name: 'LinkedIn Personal', color: 'bg-[#0A66C2]', icon: 'in' },
  instagram_business: { name: 'Instagram Business', color: 'bg-[#E4405F]', icon: 'ig' },
  facebook_page: { name: 'Facebook Page', color: 'bg-[#1877F2]', icon: 'fb' },
  facebook_group: { name: 'Facebook Group', color: 'bg-[#1877F2]', icon: 'fb' },
  twitter: { name: 'Twitter/X', color: 'bg-black', icon: 'x' },
  youtube: { name: 'YouTube', color: 'bg-[#FF0000]', icon: 'yt' },
  tiktok: { name: 'TikTok', color: 'bg-black', icon: 'tk' },
  pinterest: { name: 'Pinterest', color: 'bg-[#E60023]', icon: 'pt' },
  wordpress: { name: 'WordPress', color: 'bg-[#21759B]', icon: 'wp' },
};

export default function PlatformConnectionCard({
  connection,
  onVerify,
  onDisconnect,
  isVerifying = false,
  isDisconnecting = false,
}: PlatformConnectionCardProps) {
  const { t } = useTranslation();
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const config = PLATFORM_CONFIG[connection.platform] || {
    name: connection.platform,
    color: 'bg-gray-600',
    icon: '??',
  };

  const handleConfirmDisconnect = () => {
    onDisconnect(connection.id);
    setShowDisconnectModal(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  return (
    <>
      <div className="border-2 border-black bg-white dark:bg-gray-950 dark:border-gray-700 p-6">
        {/* Platform Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {/* Platform Icon */}
            <div
              className={`${config.color} text-white w-12 h-12 flex items-center justify-center text-sm font-bold border-2 border-black dark:border-gray-700`}
            >
              {config.icon.toUpperCase()}
            </div>

            {/* Platform Name & Account */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {config.name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {connection.platformAccountName || connection.platformUsername || t('api.connection.noAccountName')}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div>
            {connection.configurationStatus === 'pending_configuration' ? (
              <span className="inline-block px-3 py-1 bg-yellow-400 text-black text-sm font-bold border-2 border-black dark:border-gray-700">
                {t('api.connection.status.pendingConfiguration')}
              </span>
            ) : connection.isActive ? (
              <span className="inline-block px-3 py-1 bg-green-400 text-black text-sm font-bold border-2 border-black dark:border-gray-700">
                {t('api.connection.status.active')}
              </span>
            ) : (
              <span className="inline-block px-3 py-1 bg-red-400 text-white text-sm font-bold border-2 border-black dark:border-gray-700">
                {t('api.connection.status.inactive')}
              </span>
            )}
          </div>
        </div>

        {/* Connection Details */}
        <div className="space-y-2 mb-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">{t('api.connection.connectedAt')}:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatDate(connection.connectedAt)}
            </span>
          </div>

          {connection.lastUsedAt && (
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">{t('api.connection.lastUsedAt')}:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatDate(connection.lastUsedAt)}
              </span>
            </div>
          )}

          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">{t('api.connection.verified')}:</span>
            <span className={`font-medium ${connection.isVerified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {connection.isVerified ? t('common.yes') : t('common.no')}
            </span>
          </div>

          {connection.platformUserId && (
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">{t('api.connection.platformId')}:</span>
              <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                {connection.platformUserId}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        {connection.configurationStatus !== 'pending_configuration' && (
          <div className="flex space-x-3 pt-4 border-t-2 border-black dark:border-gray-700">
            <Button
              onClick={() => onVerify(connection.id)}
              disabled={isVerifying}
              variant="secondary"
              className="flex-1"
            >
              {isVerifying ? t('api.connection.verifying') : t('api.connection.verify')}
            </Button>

            <Button
              onClick={() => setShowDisconnectModal(true)}
              disabled={isDisconnecting}
              variant="primary"
              className="flex-1 bg-red-500 hover:bg-red-600"
            >
              {isDisconnecting ? t('api.connection.disconnecting') : t('api.connection.disconnect')}
            </Button>
          </div>
        )}
      </div>

      {/* Disconnect Confirmation Modal */}
      <Modal
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        title={t('api.connection.disconnectConfirmTitle')}
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            {t('api.connection.disconnectConfirmMessage', {
              platform: config.name,
              account: connection.platformAccountName || connection.platformUsername || t('api.connection.thisAccount'),
            })}
          </p>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              {t('api.connection.disconnectWarning')}
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={() => setShowDisconnectModal(false)}
              variant="secondary"
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmDisconnect}
              variant="primary"
              className="flex-1 bg-red-500 hover:bg-red-600"
            >
              {t('api.connection.confirmDisconnect')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
